import React, { useState, useEffect, useCallback, useRef } from "react";
import { TooltipContainer } from "./Tooltip.jsx";
import { twClassNames } from "../helpers/twClassNames";
import type { SSRReactComponent } from "../../../state/types";

const LINE_CLAMP_CLASSES: Record<number, string> = {
    1: "line-clamp-1",
    2: "line-clamp-2",
    3: "line-clamp-3",
    4: "line-clamp-4",
    5: "line-clamp-5",
    6: "line-clamp-6"
};

interface DelayedTooltipProps {
    content: string;
    delay?: [number, number] | string;
    placement?: string;
    className?: string;
    allowImmediateCopy?: boolean | string;
    readOnly?: boolean | string;
    lineClamp?: 1 | 2 | 3 | 4 | 5 | 6;
}

export const DelayedTooltip: SSRReactComponent = ({
    content,
    delay = [800, 0],
    placement = "top",
    className,
    allowImmediateCopy = false,
    readOnly = false,
    lineClamp
}: DelayedTooltipProps) => {
    const [isCopied, setIsCopied] = useState(false);
    const [isTooltipVisible, setIsTooltipVisible] = useState(false);
    const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Parse delay if it comes as a JSON string (from data attributes in Handlebars)
    const parsedDelay = React.useMemo(() => {
        if (typeof delay === "string") {
            try {
                return JSON.parse(delay);
            } catch {
                return [800, 0];
            }
        }
        return delay;
    }, [delay]);

    // Parse allowImmediateCopy if it comes as a string (from data attributes in Handlebars)
    const shouldAllowImmediateCopy = React.useMemo(() => {
        if (typeof allowImmediateCopy === "string") {
            return allowImmediateCopy === "true";
        }
        return allowImmediateCopy;
    }, [allowImmediateCopy]);

    // Parse readOnly if it comes as a string (from data attributes in Handlebars)
    const isReadOnly = React.useMemo(() => {
        if (typeof readOnly === "string") {
            return readOnly === "true";
        }
        return readOnly;
    }, [readOnly]);

    useEffect(() => {
        if (isCopied) {
            const timer = setTimeout(() => {
                setIsCopied(false);
            }, 2000);
            return () => clearTimeout(timer);
        }
    }, [isCopied]);

    // Cleanup hover timeout on unmount
    useEffect(() => {
        return () => {
            if (hoverTimeoutRef.current) {
                clearTimeout(hoverTimeoutRef.current);
            }
        };
    }, []);

    const handleMouseEnter = useCallback(() => {
        const delayMs = Array.isArray(parsedDelay) ? parsedDelay[0] : parsedDelay;
        hoverTimeoutRef.current = setTimeout(() => {
            setIsTooltipVisible(true);
        }, delayMs);
    }, [parsedDelay]);

    const handleMouseLeave = useCallback(() => {
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
            hoverTimeoutRef.current = null;
        }
        setIsTooltipVisible(false);
    }, []);

    const handleClick = useCallback(
        async (e: React.MouseEvent) => {
            if (isReadOnly) {
                return;
            }

            // Copy immediately if allowImmediateCopy is true, or wait for tooltip visibility
            if (shouldAllowImmediateCopy || isTooltipVisible) {
                e.preventDefault();
                e.stopPropagation();
                try {
                    await navigator.clipboard.writeText(content);
                    setIsCopied(true);
                } catch (err) {
                    // Silently fail if clipboard access is denied
                }
            }
            // Otherwise, let the click propagate to perform original action
        },
        [content, isTooltipVisible, shouldAllowImmediateCopy, isReadOnly]
    );

    return (
        <TooltipContainer
            title={
                isReadOnly ? (
                    content
                ) : (
                    <span className="flex items-center gap-1.5">
                        <i className="far fa-copy text-xs text-gray-200" />
                        {content}
                    </span>
                )
            }
            type="titleOnly"
            placement={placement}
            delay={parsedDelay}
            customButton={
                <span
                    className={twClassNames(
                        "flex min-w-0 select-none items-center gap-1.5",
                        !isReadOnly &&
                            (isTooltipVisible || shouldAllowImmediateCopy) &&
                            "cursor-copy",
                        isCopied ? "text-success" : "text-primary",
                        className
                    )}
                    onClick={handleClick}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                >
                    <span className="relative flex min-w-0 overflow-hidden">
                        <span
                            className={twClassNames(
                                lineClamp ? LINE_CLAMP_CLASSES[lineClamp] : "block truncate",
                                isCopied && "invisible"
                            )}
                        >
                            {content}
                        </span>
                        {isCopied && (
                            <span className="absolute inset-0 flex items-baseline">
                                Copied <i className="fas fa-check ml-1" />
                            </span>
                        )}
                    </span>
                </span>
            }
        />
    );
};

DelayedTooltip.SSRElementQuery = "[data-react='DelayedTooltip']";
